import React from "react";
import { ArrowRight, ArrowUpRight, WifiOff } from "lucide-react";
import { CONTACT, ECOSYSTEM, PLATFORM_MODULES } from "../../data/content";
import { Reveal, SectionHeading } from "../ui";

const KntoMark = ({ className = "h-6 w-6" }) => (
  <img
    src="https://kntopass.com/static/media/logo-icon.cf6fdab9b438880aa7701c4e1f35ec83.svg"
    alt=""
    className={className}
    loading="lazy"
  />
);

const Platform = () => (
  <section id="platform" className="section overflow-hidden">
    <div className="absolute left-0 top-1/3 -z-10 h-[30rem] w-[30rem] rounded-full bg-knto-700/20 blur-[140px]" />

    <div className="container">
      <SectionHeading
        eyebrow="Flagship product"
        title={
          <>
            <span className="inline-flex items-center gap-3 align-middle">
              <KntoMark className="h-10 w-10 sm:h-12 sm:w-12" />
              KNTO Pass
            </span>
            <span className="block text-ink-400">Smart access & identity infrastructure.</span>
          </>
        }
        subtitle="Manage access, memberships, visitors, invitations and attendance from one centralized ecosystem built for scale."
      />

      {/* Bento grid of modules */}
      <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PLATFORM_MODULES.map(({ icon: Icon, title, desc, span = "" }, i) => (
          <Reveal
            key={title}
            delay={(i % 4) * 80}
            className={`card card-hover group relative overflow-hidden p-7 ${span}`}
          >
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-600/0 blur-2xl transition duration-500 group-hover:bg-brand-600/20" />
            <Icon className="h-6 w-6 text-brand-500" />
            <h3 className="mt-5 font-display text-lg font-semibold text-white">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-400">{desc}</p>
          </Reveal>
        ))}
      </div>

      {/* Ecosystem */}
      <Reveal className="mt-24 overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-ink-900 to-ink-950 p-8 sm:p-12">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-knto-400" />
              Hybrid architecture
            </span>
            <h3 className="mt-5 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Cloud when you have it. Gates that work when you don't.
            </h3>
            <p className="mt-5 leading-relaxed text-ink-300">
              KNTO Pass combines cloud services, local synchronization nodes and offline validation, so access keeps
              flowing even when the internet doesn't.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
              <WifiOff className="h-5 w-5" />
              Access validation continues without connectivity.
            </div>
          </div>

          <ol className="relative grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {ECOSYSTEM.map(({ icon: Icon, title, desc }, i) => (
              <li key={title} className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-knto-700/30 text-knto-400 ring-1 ring-inset ring-knto-500/30">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-sm font-semibold text-ink-500">0{i + 1}</span>
                </div>
                <h4 className="mt-5 font-semibold text-white">{title}</h4>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-400">{desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </Reveal>

      <Reveal className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <a href={CONTACT.kntoUrl} target="_blank" rel="noopener noreferrer" className="btn-knto btn-lg">
          <KntoMark className="h-5 w-5" /> Visit kntopass.com <ArrowUpRight className="h-4 w-4" />
        </a>
        <a href="#contact" className="btn-ghost btn-lg">
          Request a walkthrough <ArrowRight className="h-4 w-4" />
        </a>
      </Reveal>
    </div>
  </section>
);

export default Platform;
